import type {
  CheckUsernameResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginRequest,
  LoginResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  TokenInfoResponse,
} from "@onemileauto/shared-types";

/**
 * Dedicated fetch calls for /api/auth/* — deliberately NOT going through
 * lib/api-client.ts's apiFetch(), which auto-redirects to /login on a 401
 * (see that file). These endpoints need to show their own inline error
 * state (wrong password, expired token, rate-limited) rather than bouncing
 * the user back to the very page they're already on.
 */

export class AuthApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new AuthApiError((data as { detail?: string }).detail ?? res.statusText, res.status);
  }
  return data as T;
}

function authPost<T>(path: string, body: unknown): Promise<T> {
  return authFetch<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function login(username: string, password: string): Promise<LoginResponse> {
  const body: LoginRequest = { username, password };
  return authPost<LoginResponse>("/api/auth/login", body);
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST" });
}

export function requestPasswordReset(email: string): Promise<ForgotPasswordResponse> {
  const body: ForgotPasswordRequest = { email };
  return authPost<ForgotPasswordResponse>("/api/auth/forgot-password", body);
}

export function resetPassword(token: string, password: string, username?: string): Promise<ResetPasswordResponse> {
  const body: ResetPasswordRequest = { token, password, username };
  return authPost<ResetPasswordResponse>("/api/auth/reset-password", body);
}

/** Non-consuming — tells the reset-password page whether to show the "choose a username" step before the user submits anything. See routes/auth.ts's GET /api/auth/token-info. */
export function getResetTokenInfo(token: string): Promise<TokenInfoResponse> {
  return authFetch<TokenInfoResponse>(`/api/auth/token-info?token=${encodeURIComponent(token)}`);
}

/** Live availability feedback while a user is choosing a username during account setup. Always resolves (even for a malformed username — see routes/auth.ts) rather than throwing, since that's a normal mid-typing state, not an error. */
export function checkUsernameAvailability(username: string): Promise<CheckUsernameResponse> {
  return authFetch<CheckUsernameResponse>(`/api/auth/check-username?username=${encodeURIComponent(username)}`);
}
